import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Badge,
  Card,
} from "react-bootstrap";
import { FaPlay, FaCheckCircle, FaTimesCircle, FaExclamationTriangle } from "react-icons/fa";
import SchoolRollNumberInput from "./SchoolRollNumberInput";
import { triggerThresholdCheckForSchool, getCommunicationRules } from "../services/APIClient";

const TestSchoolMessageModal = ({ show, onHide, onTestComplete }) => {
  const [schoolId, setSchoolId] = useState("");
  const [schoolName, setSchoolName] = useState("No school selected");
  const [schoolRollNumber, setSchoolRollNumber] = useState("");
  const [rules, setRules] = useState([]);
  const [selectedRuleId, setSelectedRuleId] = useState("all");
  const [loadingRules, setLoadingRules] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [error, setError] = useState(null);

  // Load rules when modal opens
  useEffect(() => {
    if (show) {
      loadRules();
      // Reset state
      setTestResults(null);
      setError(null);
    }
  }, [show]);

  const loadRules = async () => {
    setLoadingRules(true);
    try {
      const rulesData = await getCommunicationRules({
        filter: { limit: 100 },
      });
      setRules(Array.isArray(rulesData) ? rulesData : []);
    } catch (err) {
      console.error("Failed to load rules:", err);
      setError("Failed to load rules. Please try again.");
    } finally {
      setLoadingRules(false);
    }
  };

  const handleSchoolIdRetrieved = (id) => {
    setSchoolId(id);
    setTestResults(null); // Clear previous results
  };

  const handleSchoolNameRetrieved = (name, rollNumber) => {
    setSchoolName(name);
    setSchoolRollNumber(rollNumber);
  };

  const handleRunTest = async () => {
    if (!schoolId) {
      setError("Please select a school first.");
      return;
    }

    setTesting(true);
    setError(null);
    setTestResults(null);

    try {
      const ruleIdParam = selectedRuleId === "all" ? undefined : selectedRuleId;
      const result = await triggerThresholdCheckForSchool(schoolId, ruleIdParam);
      setTestResults(result);
      
      if (onTestComplete) {
        onTestComplete(result);
      }
    } catch (err) {
      console.error("Test failed:", err);
      setError(err.message || "Failed to run test. Please try again.");
    } finally {
      setTesting(false);
    }
  };

  const handleClose = () => {
    setSchoolId("");
    setSchoolName("No school selected");
    setSchoolRollNumber("");
    setSelectedRuleId("all");
    setTestResults(null);
    setError(null);
    onHide();
  };

  const activeRules = rules.filter((r) => r.active);

  const renderResultCard = (result, index) => {
    const isSuccess = result.success !== false && !result.error;
    const meetsThreshold = result.meetsThreshold === true;
    const alreadyNotified = result.alreadyNotified === true;

    let variant = "secondary";
    let icon = null;
    let title = "Unknown";

    if (result.error) {
      variant = "danger";
      icon = <FaTimesCircle />;
      title = "Error";
    } else if (alreadyNotified) {
      variant = "warning";
      icon = <FaExclamationTriangle />;
      title = "Already Notified";
    } else if (meetsThreshold && isSuccess) {
      variant = "success";
      icon = <FaCheckCircle />;
      title = "Threshold Met - Notification Sent";
    } else if (!meetsThreshold) {
      variant = "secondary";
      icon = <FaTimesCircle />;
      title = "Threshold Not Met";
    }

    return (
      <Card key={index} className="mb-3" border={variant}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="d-flex align-items-center">
              <span className={`text-${variant} me-2`}>{icon}</span>
              <strong>{title}</strong>
            </div>
            <Badge bg={variant}>{result.eventType || "Unknown"}</Badge>
          </div>

          {result.message && (
            <Alert variant={variant} className="mb-2 py-2">
              {result.message}
            </Alert>
          )}

          <div className="small">
            {result.percentage !== undefined && (
              <div className="mb-1">
                <strong>Completion Rate:</strong> {result.percentage}%
                {result.threshold !== undefined && (
                  <span className="text-muted"> (Threshold: {result.threshold}%)</span>
                )}
              </div>
            )}

            {result.notificationsSent !== undefined && (
              <div className="mb-1">
                <strong>Notifications Sent:</strong> {result.notificationsSent}
              </div>
            )}

            {result.existingEventId && (
              <div className="mb-1">
                <strong>Existing Event ID:</strong> {result.existingEventId}
                <br />
                <small className="text-muted">
                  Triggered at: {new Date(result.triggeredAt).toLocaleString()}
                </small>
              </div>
            )}

            {result.eventId && (
              <div className="mb-1">
                <strong>New Event ID:</strong> {result.eventId}
              </div>
            )}

            {result.errors && result.errors.length > 0 && (
              <div className="mt-2">
                <strong className="text-danger">Errors:</strong>
                <ul className="mb-0 mt-1">
                  {result.errors.map((err, i) => (
                    <li key={i} className="text-danger">
                      {err}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaPlay className="me-2" />
          Test Individual School
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* School Selection */}
        <div className="mb-4">
          <SchoolRollNumberInput
            onSchoolIdRetrieved={handleSchoolIdRetrieved}
            schoolNameRetrieved={handleSchoolNameRetrieved}
          />
          
          {schoolId && (
            <Alert variant="info" className="mt-3 mb-0">
              <strong>Selected School:</strong> {schoolName}
              <br />
              <strong>Roll Number:</strong> {schoolRollNumber}
              <br />
              <strong>School ID:</strong> {schoolId}
            </Alert>
          )}
        </div>

        {/* Rule Selection */}
        <Form.Group className="mb-4">
          <Form.Label className="fw-semibold">Rule to Test</Form.Label>
          {loadingRules ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Loading rules...</span>
            </div>
          ) : (
            <Form.Select
              value={selectedRuleId}
              onChange={(e) => setSelectedRuleId(e.target.value)}
              disabled={!schoolId}
            >
              <option value="all">All Active Rules ({activeRules.length})</option>
              {activeRules.map((rule) => (
                <option key={rule.id} value={rule.id}>
                  {rule.eventType} - {rule.threshold}% threshold
                </option>
              ))}
            </Form.Select>
          )}
          <Form.Text className="text-muted">
            Select a specific rule or test all active rules for this school
          </Form.Text>
        </Form.Group>

        {/* Run Test Button */}
        <div className="d-grid mb-4">
          <Button
            variant="primary"
            size="lg"
            onClick={handleRunTest}
            disabled={!schoolId || testing}
          >
            {testing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Running Test...
              </>
            ) : (
              <>
                <FaPlay className="me-2" />
                Run Test
              </>
            )}
          </Button>
        </div>

        {/* Test Results */}
        {testResults && (
          <div>
            <h5 className="mb-3">Test Results</h5>
            
            <Alert variant="info" className="mb-3">
              <strong>School:</strong> {testResults.schoolName}
              <br />
              <strong>Rules Checked:</strong> {testResults.rulesChecked}
            </Alert>

            {testResults.results && testResults.results.length > 0 ? (
              testResults.results.map((result, index) =>
                renderResultCard(result, index)
              )
            ) : (
              <Alert variant="secondary">
                No results returned. The school may not have any data for the selected rule(s).
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

TestSchoolMessageModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onTestComplete: PropTypes.func,
};

export default TestSchoolMessageModal;